import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'wouter';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { 
  ArrowLeft,
  ChevronLeft, 
  ChevronRight, 
  CheckCircle2, 
  ExternalLink, 
  FileText, 
  Info,
  Maximize, 
  Minimize,
  Play, 
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

// Key takeaway component for highlighting important points
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6 rounded-r-md">
      <div className="flex">
        <CheckCircle2 className="h-6 w-6 text-blue-500 flex-shrink-0 mr-3 mt-1" />
        <div className="text-blue-800">{children}</div>
      </div>
    </div>
  );
};

// Slide interface
interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
  image?: string;
}

// Slideshow component
const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const slideshowRef = useRef<HTMLDivElement>(null);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (slideshowRef.current?.requestFullscreen) {
        slideshowRef.current.requestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  // Add fullscreen change event listener
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (slides.length === 0) return null;

  return (
    <div 
      ref={slideshowRef} 
      className={`relative overflow-hidden rounded-lg border ${isFullscreen ? 'bg-white flex flex-col h-screen' : 'bg-gray-50'}`}
    >
      {/* Slide content */}
      <div className="relative flex-grow">
        <div className={`p-6 ${isFullscreen ? 'h-[calc(100vh-70px)] overflow-y-auto' : 'max-h-[70vh] overflow-y-auto'} thin-scrollbar`}>
          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-xl font-medium text-gray-900">
              {slides[currentSlide].title}
            </h3>
            <div className="flex space-x-2">
              <button 
                onClick={toggleFullscreen}
                className="p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
              </button>
              
              {isFullscreen && (
                <button 
                  onClick={() => document.exitFullscreen()}
                  className="p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
          
          <div className="prose max-w-none">
            {slides[currentSlide].content}
          </div>

          {slides[currentSlide].image && (
            <div className="flex justify-center mt-4">
              <img src={slides[currentSlide].image} alt="Slide image" className="max-w-full rounded" />
            </div>
          )}
        </div>
      </div>

      {/* Navigation controls - now always visible in fullscreen too */}
      <div className={`flex items-center justify-between p-4 border-t bg-white ${isFullscreen ? 'mt-auto' : ''}`}>
        <div className="text-sm text-gray-500">
          Slide {currentSlide + 1} of {slides.length}
        </div>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={prevSlide}
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={nextSlide}
            aria-label="Next slide"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Video placeholder component
const VideoPlaceholder = () => {
  return (
    <div className="bg-gray-100 rounded-xl overflow-hidden my-10">
      <div className="aspect-video bg-gray-200 flex items-center justify-center relative">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-indigo-500/20"></div>
        <div className="text-center">
          <div className="bg-blue-600 text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg hover:bg-blue-700 cursor-pointer transition-colors">
            <Play className="h-8 w-8" />
          </div>
          <p className="text-gray-700 font-medium">Watch Full Lecture Video</p>
          <p className="text-sm text-gray-500 mt-1">45 minutes · Premium Content</p>
        </div>
      </div>
    </div>
  );
};

function CellularFrameworkLongevity() {
  const [_, navigate] = useLocation();
  
  
  // Lecture slides data
  const lectureSlides: Slide[] = [
    {
      id: 1,
      title: "Introduction: The SHIFT Framework",
      content: (
        <>
          <p>Longevity medicine seeks to extend healthspan by addressing root causes of cellular aging. To guide clinicians through this complex landscape, we introduce the <strong>SHIFT</strong> framework – five interlocking domains that govern the cellular basis of health and disease:</p>
          
          <ul className="list-disc list-inside space-y-2 my-4 ml-4">
            <li><strong>S</strong>ensing & metabolic fuel-gauging pathways monitor nutrients and energy (e.g. insulin/IGF-1, mTOR, AMPK, sirtuins)</li>
            <li><strong>H</strong>omeostatic defense & repair mechanisms preserve integrity (e.g. NRF2, p53, autophagy)</li>
            <li><strong>I</strong>nflammation & immune tone balance immune surveillance with inflammatory control</li>
            <li><strong>F</strong>ibrosis & structural remodeling maintain tissue architecture and prevent pathological remodeling</li>
            <li><strong>T</strong>imekeeping & chronobiology synchronize cellular processes with environmental rhythms</li>
          </ul>
          
          <p>This SHIFT framework helps organize the vast literature on cellular aging into actionable domains for clinical intervention. In this lecture, we'll explore each domain, examine key research findings, and discuss evidence-based interventions that target these fundamental cellular processes.</p>
          
          <KeyTakeaway>
            The SHIFT framework provides clinicians with a practical structure for understanding and addressing the cellular mechanisms of aging and longevity.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 2,
      title: "S – Sensing & Metabolic Fuel-Gauging: Overview",
      content: (
        <>
          <p>Cells continuously sense energy and nutrient availability via conserved pathways – notably insulin/IGF-1 signaling, the mechanistic target of rapamycin (mTOR), AMP-activated kinase (AMPK), and the sirtuins. These pathways form a metabolic fuel gauge that modulates growth, repair, and metabolism.</p>
          
          <p>In youth, robust nutrient signaling promotes growth and reproduction, but chronically high activity (e.g. hyperinsulinemia or overnutrition) in later life may accelerate aging processes. Longevity research shows that periodically "dialing down" these pathways can promote cellular maintenance and potentially extend lifespan.</p>
          
          <div className="bg-gray-100 p-4 my-4 rounded-md">
            <h3 className="text-lg font-medium mb-3">Key Sensing Pathways</h3>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong>Insulin/IGF-1 Signaling:</strong> Influences growth, metabolism, and lifespan across species</li>
              <li><strong>mTOR (mechanistic Target of Rapamycin):</strong> Integrates nutrient signals to regulate protein synthesis and autophagy</li>
              <li><strong>AMPK (AMP-activated Protein Kinase):</strong> Senses low cellular energy and initiates catabolic processes</li>
              <li><strong>Sirtuins:</strong> NAD⁺-dependent enzymes linking metabolic state to gene expression and cellular maintenance</li>
            </ul>
          </div>
          
          <p>These pathways interact extensively – for example, AMPK activation (as during exercise or fasting) inhibits mTOR and activates sirtuins, while insulin/IGF-1 signaling activates mTOR and can suppress AMPK. Understanding these interactions helps explain why interventions like intermittent fasting may simultaneously affect multiple longevity pathways.</p>
          
          <KeyTakeaway>
            Nutrient sensing pathways evolved to optimize survival in environments with variable food availability. Modern constant overnutrition may contribute to accelerated aging by keeping these pathways chronically activated.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 3,
      title: "S – Sensing & Metabolic Fuel-Gauging: Key Research Findings",
      content: (
        <>
          <p>The research connecting nutrient sensing pathways to longevity is robust across multiple model organisms and is beginning to show translation to human health:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-blue-800 mb-2">IGF-1 Signaling and Longevity</h3>
              <p>In animal models from worms to mice, reduced insulin/IGF-1 signaling consistently extends lifespan. Human studies show similar trends – female centenarians (Ashkenazi Jewish cohort) have a higher prevalence of IGF-1 receptor mutations that modestly reduce IGF signaling. This association strengthens the evolutionary conservation of this pathway's influence on longevity.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-indigo-800 mb-2">mTOR and Caloric Restriction Mimetics</h3>
              <p>Lifelong caloric restriction (CR) can extend rodent lifespans by 30-50%, partly via mTOR inhibition. Direct mTOR inhibition with rapamycin reproduces many CR benefits – a landmark study showed middle-aged mice given rapamycin had ~25% extension in median lifespan. Human trials with mTOR inhibitors show promising improvements in immune function and vaccine response in older adults.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-purple-800 mb-2">AMPK Activation and Metformin</h3>
              <p>Metformin, a widely used AMPK-activator, has emerged as a candidate longevity drug. A large UK study found diabetic patients on metformin outlived matched non-diabetics by 15%. The TAME (Targeting Aging with Metformin) trial aims to test if metformin can delay age-related diseases in non-diabetic individuals. Multiple observational studies show metformin users have lower cancer incidence and mortality.</p>
            </div>
            <div className="bg-pink-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-pink-800 mb-2">Sirtuins and NAD⁺ Boosters</h3>
              <p>The sirtuin family (SIRT1-7) links cellular redox state to gene expression. Preclinical studies show boosting sirtuin activity (via resveratrol or NAD⁺ precursors) improves metabolism in lower organisms. Human trials with NAD⁺ precursors like nicotinamide riboside (NR) show increased NAD⁺ levels and improvements in cardiovascular parameters, though long-term clinical outcomes remain under investigation.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Research evidence consistently points to nutrient sensing pathways as key modulators of longevity. Interventions that periodically downregulate these pathways may promote cellular maintenance programs that delay aging.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 4,
      title: "S – Sensing & Metabolic Fuel-Gauging: Clinical Interventions",
      content: (
        <>
          <p>Based on current evidence, several interventions targeting sensing pathways can be considered in clinical practice, with varying levels of evidence strength:</p>
          
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 my-6">
            <h3 className="text-xl font-medium text-blue-900 mb-4">Evidence-Based Interventions for Sensing Pathways</h3>
            
            <div className="space-y-5">
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Caloric Restriction & Fasting Protocols</h4>
                  <p className="text-gray-700 mb-2">Periodic fasting or 20–40% daily calorie reduction reliably improves cardiometabolic markers (blood pressure, insulin sensitivity, lipid profile). The 2-year CALERIE trial in non-obese adults demonstrated sustained weight loss, lower inflammation, and gene expression changes mirroring long-lived CR mice.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Consider time-restricted eating (16:8 pattern) or 5:2 intermittent fasting as more sustainable approaches than chronic calorie restriction. Start with 12-hour overnight fasts and gradually extend the fasting window.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Metformin</h4>
                  <p className="text-gray-700 mb-2">Typical dose: 1.5–2 g/day (extended-release often preferred to reduce GI side effects). Primarily indicated for diabetes, but sometimes prescribed off-label for insulin-resistant but non-diabetic patients with careful monitoring.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Reasonable to consider in patients with prediabetes/metabolic syndrome who fail lifestyle interventions. Monitor B12 levels with long-term use. Contraindicated in severe renal or hepatic impairment.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Rapamycin & mTOR Inhibitors</h4>
                  <p className="text-gray-700 mb-2">Not yet approved for anti-aging but available off-label. Experimental doses being studied: rapamycin 5–10 mg once weekly, or everolimus 1.5 mg daily (both much lower than immunosuppressive regimens).</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Given potent effects, not recommended for routine use outside of clinical trials. The PEARL trial and other studies in healthy older adults will help establish safety profiles and effects on aging biomarkers.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">4</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">NAD⁺ Boosters</h4>
                  <p className="text-gray-700 mb-2">NAD⁺ precursors like nicotinamide riboside (NR) or nicotinamide mononucleotide (NMN) raise cellular NAD⁺ levels, potentially activating sirtuins. Typical doses in human studies: NR 500-1000 mg/day, NMN 250-1200 mg/day.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Consider in patients with fatigue, metabolic syndrome, or mitochondrial dysfunction. Human evidence for long-term benefits is still emerging, so recommend time-limited trials with careful monitoring.</p>
                </div>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            Interventions targeting sensing pathways range from well-established lifestyle approaches (fasting protocols) to more experimental pharmacological options. The risk-benefit profile should guide clinical decisions, with lower-risk interventions preferred for most patients.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 5,
      title: "H – Homeostatic Defense & Repair: Overview",
      content: (
        <>
          <p>This domain covers the cell's built-in maintenance crew – systems that detect damage and restore equilibrium. These mechanisms are essential for cellular longevity and become progressively less efficient with age, contributing to the accumulation of cellular damage characteristic of aging.</p>
          
          <div className="bg-gray-100 p-4 my-4 rounded-md">
            <h3 className="text-lg font-medium mb-3">Key Defense & Repair Systems</h3>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong>NRF2 pathway:</strong> A transcription factor that upregulates antioxidant and detoxification enzymes</li>
              <li><strong>DNA damage response:</strong> Including p53 and p16^INK4a, which enforce checkpoints and suppress cancer</li>
              <li><strong>Autophagy and mitophagy:</strong> Cellular recycling processes that remove damaged proteins and organelles</li>
              <li><strong>Heat shock proteins:</strong> Molecular chaperones that help maintain proper protein folding</li>
              <li><strong>Antioxidant systems:</strong> Including superoxide dismutase, catalase, and glutathione peroxidase</li>
            </ul>
          </div>
          
          <p>These defense and repair mechanisms work in concert to maintain cellular integrity in the face of constant challenges from both endogenous metabolism and external stressors. Their efficiency determines how well cells manage damage over time.</p>
          
          <p>Importantly, these systems often demonstrate hormetic properties – mild, intermittent stress can upregulate these pathways, potentially enhancing cellular resilience. This principle underlies many longevity interventions that apply controlled stressors to strengthen cellular defenses.</p>
          
          <KeyTakeaway>
            Robust homeostatic defense and repair mechanisms are essential for cellular longevity. Many age-related diseases involve the progressive failure of these systems to maintain cellular integrity and function.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 6,
      title: "H – Homeostatic Defense & Repair: Key Research Findings",
      content: (
        <>
          <p>Research has demonstrated the critical importance of cellular defense and repair pathways in health and longevity:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-blue-800 mb-2">NRF2 Activation</h3>
              <p>NRF2 is often called the "master regulator" of cytoprotective responses, governing genes that combat oxidative stress and remove toxins. Its activity wanes with age, contributing to cellular wear-and-tear. In animal models, boosting NRF2 extends healthspan – overexpression in worms and mice increases resistance to toxins and can modestly extend lifespan. Dietary NRF2 activators like sulforaphane from broccoli sprouts have shown anti-inflammatory and detoxification benefits in human studies.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-indigo-800 mb-2">p53, p16, and DNA Damage Responses</h3>
              <p>The p53 protein, dubbed the "guardian of the genome," prevents cancer by halting the cell cycle or inducing apoptosis in damaged cells. This balances cancer suppression against stem cell preservation. Mice engineered for hyperactive p53 show premature aging phenotypes, while those with enhanced but regulated p53 show cancer resistance without premature aging. Human genomic studies confirm p53 pathway variants influence both cancer risk and longevity.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-purple-800 mb-2">Autophagy & Mitophagy</h3>
              <p>Autophagy is the cell's self-recycling program, crucial for clearing damaged proteins and organelles. With age, autophagy efficiency drops, leading to accumulation of dysfunctional components. Genetic studies show autophagy is essential for longevity – long-lived mutant worms and flies almost always have upregulated autophagy. In humans, increased autophagy markers correlate with healthier metabolic profiles, and compounds that enhance autophagy (e.g., spermidine) show promise in early trials.</p>
            </div>
            <div className="bg-pink-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-pink-800 mb-2">Heat Shock Response</h3>
              <p>Heat shock proteins (HSPs) are molecular chaperones induced by various stressors. They help maintain proper protein folding and prevent aggregation of misfolded proteins. HSP induction decreases with age, potentially contributing to age-related proteotoxicity. Studies in centenarians reveal genetic variants associated with enhanced heat shock responses. Heat conditioning (sauna use, hot baths) induces HSPs in humans and may offer cardioprotective and neurological benefits.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Molecular defense and repair systems are essential for longevity. Research increasingly suggests that these pathways can be deliberately activated to enhance cellular resilience and potentially extend healthspan.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 7,
      title: "H – Homeostatic Defense & Repair: Clinical Interventions",
      content: (
        <>
          <p>Several evidence-based interventions can enhance cellular defense and repair mechanisms:</p>
          
          <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-lg p-6 my-6">
            <h3 className="text-xl font-medium text-green-900 mb-4">Evidence-Based Interventions for Defense & Repair</h3>
            
            <div className="space-y-5">
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-green-800 font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">NRF2 Activators</h4>
                  <p className="text-gray-700 mb-2">Sulforaphane from broccoli sprouts is a leading example: effective dose ~20–40 mg daily (achievable via ~1/2 cup of fresh broccoli sprouts or standardized supplements). Other dietary NRF2 activators include curcumin, EGCG from green tea, and resveratrol.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Advise patients to incorporate cruciferous vegetables regularly. For those seeking more targeted intervention, sulforaphane supplements with documented bioavailability can be considered, especially for inflammatory conditions.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-green-800 font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Autophagy Enhancers</h4>
                  <p className="text-gray-700 mb-2">Lifestyle interventions like exercise and intermittent fasting reliably induce autophagy. For supplement approaches, spermidine (derived from wheat germ or fermented foods, ~2-5mg/day) has shown promise in early human studies, improving cardiovascular and cognitive parameters.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Encourage overnight fasts of at least 12 hours and high-intensity interval training to activate AMPK and autophagy machinery. Consider spermidine-rich foods (wheat germ, aged cheese, fermented soybeans) or supplements for interested patients.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-green-800 font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Heat Stress Conditioning</h4>
                  <p className="text-gray-700 mb-2">Regular sauna use (4-7 sessions/week, 20 minutes at 80-100°C) has been associated with reduced cardiovascular and all-cause mortality in observational studies. Heat exposure induces heat shock proteins that maintain proteostasis.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> For patients without contraindications, recommend regular sauna sessions or hot baths (40-42°C for 30 minutes). Start with shorter sessions and lower temperatures for heat-sensitive individuals. Contrast therapy (alternating hot/cold exposure) may provide additional benefits.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-green-800 font-bold">4</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">DNA Repair Support</h4>
                  <p className="text-gray-700 mb-2">Direct enhancement of DNA repair remains challenging, but ensuring adequate micronutrients is essential. Focus on vitamins B12 and folate for DNA synthesis, magnesium and zinc for repair enzyme function, and avoiding excessive environmental mutagens.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Assess micronutrient status and optimize as needed. Glycine supplementation (3 g/day) shows promise as glycine is required for glutathione production and DNA repair processes. Smoking cessation represents perhaps the single greatest "genomic stability" intervention.</p>
                </div>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            Enhancing cellular defense and repair mechanisms offers a promising approach to maintaining cellular health with age. Many interventions are accessible lifestyle modifications with favorable risk-benefit profiles.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 8,
      title: "I – Inflammation & Immune Tone: Overview",
      content: (
        <>
          <p>Inflammation is a fundamental biological response that protects against infection and injury but can become maladaptive when chronically activated. The balance between appropriate immune surveillance and excessive inflammatory activation – which we term "immune tone" – is critical for health and longevity.</p>
          
          <div className="bg-gray-100 p-4 my-4 rounded-md">
            <h3 className="text-lg font-medium mb-3">Key Inflammatory and Immune Processes</h3>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong>Acute inflammation:</strong> The coordinated response to pathogens or tissue damage</li>
              <li><strong>Chronic inflammation:</strong> Persistent low-grade activation that contributes to many age-related diseases</li>
              <li><strong>Inflammaging:</strong> The progressive increase in basal inflammation with age</li>
              <li><strong>Cellular senescence:</strong> Permanently growth-arrested cells that secrete pro-inflammatory factors</li>
              <li><strong>Immunosenescence:</strong> Age-related decline in immune function and adaptability</li>
            </ul>
          </div>
          
          <p>With age, the inflammatory system tends to develop two parallel problems: (1) chronic elevation of basal inflammation, and (2) reduced capacity for precise, targeted responses to specific threats. This combination contributes to both increased tissue damage and impaired immune defense.</p>
          
          <p>Understanding the cellular mechanisms underlying these changes offers opportunities for targeted interventions that may help restore optimal immune tone – maintaining robust defenses while preventing excessive inflammatory activation.</p>
          
          <KeyTakeaway>
            Immune tone represents the balance between necessary immune surveillance and excessive inflammatory activation. Disruption of this balance contributes to many age-related diseases and is a key target for longevity interventions.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 9,
      title: "I – Inflammation & Immune Tone: Key Research Findings",
      content: (
        <>
          <p>Research has revealed the central importance of inflammatory processes in aging and age-related diseases:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-orange-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-orange-800 mb-2">Inflammaging</h3>
              <p>Multiple studies confirm that inflammatory markers (IL-6, TNF-α, CRP) increase with age even in apparently healthy individuals. This "inflammaging" predicts mortality and multiple age-related conditions. Large cohort studies show that individuals with elevated inflammatory markers have 2-3x higher risk of developing cardiovascular disease, type 2 diabetes, and certain cancers. The inflammaging phenotype correlates with epigenetic age acceleration in several human studies.</p>
            </div>
            <div className="bg-amber-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-amber-800 mb-2">Cellular Senescence</h3>
              <p>Senescent cells accumulate with age and secrete a pro-inflammatory senescence-associated secretory phenotype (SASP). Groundbreaking mouse studies showed that selectively eliminating senescent cells (with "senolytic" compounds) extends healthy lifespan by ~25% in some models and improves multiple age-related pathologies. Early human trials with senolytics show promising results for specific conditions like diabetic kidney disease and idiopathic pulmonary fibrosis.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-yellow-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-yellow-800 mb-2">Immunosenescence</h3>
              <p>Aging is associated with thymic involution, reduced naive T cell production, and accumulation of memory T cells with limited antigen specificity. Cytomegalovirus (CMV) seropositivity accelerates aspects of immunosenescence. Studies show that immunosenescent profiles predict poor vaccine response and increased infection risk in older adults. Interventions that rejuvenate thymic function or reduce exhausted T cell populations show promise in animal models.</p>
            </div>
            <div className="bg-lime-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-lime-800 mb-2">Resolution Pathways</h3>
              <p>Inflammation resolution is an active process mediated by specialized pro-resolving mediators (SPMs) derived from omega-3 fatty acids. These include resolvins, protectins, and maresins. Research shows that SPM production declines with age, potentially contributing to chronic inflammation. Supplementation with SPM precursors improves inflammatory resolution in animal models and early human studies, particularly for conditions like arthritis and periodontitis.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Chronic inflammation represents a major driver of aging and age-related diseases. Research increasingly suggests that targeting inflammatory processes, particularly cellular senescence and impaired resolution, may improve healthspan.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 10,
      title: "I – Inflammation & Immune Tone: Clinical Interventions",
      content: (
        <>
          <p>Evidence-based interventions to optimize inflammatory balance and immune function include:</p>
          
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-6 my-6">
            <h3 className="text-xl font-medium text-orange-900 mb-4">Evidence-Based Interventions for Inflammation & Immune Tone</h3>
            
            <div className="space-y-5">
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-orange-800 font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Senolytic Therapies</h4>
                  <p className="text-gray-700 mb-2">Compounds that selectively eliminate senescent cells, such as the combination of dasatinib (D) + quercetin (Q), or fisetin alone. Early human trials use intermittent dosing: D (100mg/day) + Q (1000mg/day) for 3 consecutive days every 3 months, or fisetin (20mg/kg/day) for 2 consecutive days every month.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> These approaches remain experimental. Consider only for patients with significant senescence burden (e.g., diabetes complications, idiopathic pulmonary fibrosis) and no contraindications, preferably as part of clinical trials. Dietary quercetin and fisetin from foods like apples, onions, and strawberries are safe alternatives with lower potency.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-orange-800 font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Resolution-Promoting Strategies</h4>
                  <p className="text-gray-700 mb-2">Marine omega-3 fatty acids (EPA/DHA) serve as precursors for specialized pro-resolving mediators. High-dose EPA/DHA (3-4g/day) shows anti-inflammatory effects in multiple studies. New formulations of SPMs themselves are being developed (17-HDHA, RvD1, etc.) but remain investigational.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Consider high-quality, high-dose EPA/DHA supplements for patients with inflammatory conditions. Test omega-3 index to assess baseline status and monitor response. EPA appears particularly beneficial for cardiovascular outcomes based on the REDUCE-IT trial, while DHA may have stronger neurological benefits.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-orange-800 font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Immunomodulatory Peptides</h4>
                  <p className="text-gray-700 mb-2">Thymic peptides like thymosin alpha-1 (1.6mg subcutaneously 2x/week) can help restore T-cell function and have been studied in immunosenescence, viral infections, and cancer. Other peptides with immunomodulatory effects include BPC-157 and LL-37, but they have less human clinical data.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Consider thymosin alpha-1 for patients with documented immune dysfunction, particularly poor vaccine response or frequent infections. Best used as part of a comprehensive immune support program including lifestyle optimization and nutritional support.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-orange-800 font-bold">4</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Lifestyle Anti-inflammatory Approaches</h4>
                  <p className="text-gray-700 mb-2">Regular physical activity, Mediterranean diet patterns, adequate sleep, and stress management all demonstrate anti-inflammatory effects in randomized trials. The CANTOS trial showed that targeted anti-inflammatory therapy (IL-1β inhibition) reduces cardiovascular events, validating inflammation as a treatment target.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Prescribe specific exercise programs (150 min/week moderate or 75 min/week vigorous activity), emphasize polyphenol-rich Mediterranean diet patterns, and recommend evidence-based stress reduction techniques like mindfulness-based stress reduction (MBSR). Track inflammatory markers (hs-CRP, IL-6) to assess response.</p>
                </div>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            Targeting inflammation and immune dysfunction offers promising approaches for extending healthspan. While senolytic therapies remain experimental, lifestyle modifications and certain nutritional approaches have strong evidence for safety and efficacy.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 11,
      title: "F – Fibrosis & Structural Remodeling: Overview",
      content: (
        <>
          <p>Fibrosis – the excessive accumulation of extracellular matrix components like collagen – represents a critical but often overlooked driver of aging and age-related diseases. This domain encompasses the structural changes that occur in tissues over time, affecting both the extracellular matrix and cellular architecture.</p>
          
          <div className="bg-gray-100 p-4 my-4 rounded-md">
            <h3 className="text-lg font-medium mb-3">Key Fibrosis & Structural Processes</h3>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong>Fibroblast activation:</strong> Transition of quiescent fibroblasts to matrix-producing myofibroblasts</li>
              <li><strong>Matrix metalloproteinases (MMPs):</strong> Enzymes that degrade extracellular matrix components</li>
              <li><strong>Tissue inhibitors of metalloproteinases (TIMPs):</strong> Regulators that inhibit MMP activity</li>
              <li><strong>Cross-linking:</strong> Formation of chemical bonds between extracellular matrix proteins</li>
              <li><strong>Glycation:</strong> Non-enzymatic addition of sugar molecules to proteins, particularly in the extracellular matrix</li>
            </ul>
          </div>
          
          <p>With age, the balance between matrix production and degradation often shifts toward excessive deposition and reduced turnover. This leads to increased tissue stiffness, reduced elasticity, and impaired organ function across multiple systems.</p>
          
          <p>Progressive fibrosis contributes to many age-related conditions, including cardiac and vascular stiffening, pulmonary fibrosis, chronic kidney disease, liver cirrhosis, and skin aging. Understanding the molecular drivers of these processes opens new avenues for intervention.</p>
          
          <KeyTakeaway>
            Fibrosis and structural remodeling represent fundamental contributors to age-related functional decline. Preventing excessive fibrosis and maintaining appropriate tissue architecture are crucial for preserving organ function with age.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 12,
      title: "F – Fibrosis & Structural Remodeling: Key Research Findings",
      content: (
        <>
          <p>Research has revealed the importance of fibrotic processes in aging and disease:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-blue-800 mb-2">Advanced Glycation End-products (AGEs)</h3>
              <p>AGEs result from non-enzymatic reactions between reducing sugars and proteins, particularly long-lived extracellular matrix proteins. They accumulate with age and in conditions like diabetes. Studies show that AGE accumulation correlates with vascular stiffness, diabetic complications, and skin aging. Dietary AGE restriction improves vascular function and insulin sensitivity in human trials. AGE-breaking compounds like alagebrium showed promise in animal models and early human studies.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-indigo-800 mb-2">TGF-β Signaling</h3>
              <p>Transforming growth factor-beta (TGF-β) is a master regulator of fibrosis across tissues. Its activity increases with age and in response to tissue injury. Mouse models with reduced TGF-β signaling show resistance to age-related fibrosis in multiple organs. TGF-β blockade therapeutically reverses established fibrosis in some animal models. Human genome-wide association studies link TGF-β pathway variants to longevity and fibrotic diseases.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-purple-800 mb-2">Matrix Remodeling Enzymes</h3>
              <p>The balance between matrix metalloproteinases (MMPs) and their inhibitors (TIMPs) regulates extracellular matrix turnover. This balance shifts with age toward reduced matrix degradation. Studies in long-lived species show preserved MMP/TIMP ratios compared to shorter-lived relatives. Human studies link MMP/TIMP imbalance to arterial stiffening, ventricular remodeling after myocardial infarction, and impaired wound healing in older adults.</p>
            </div>
            <div className="bg-pink-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-pink-800 mb-2">Senescent Cell Contribution</h3>
              <p>Senescent cells promote fibrosis through their secretory phenotype, which includes TGF-β, MMPs, and other factors that stimulate fibroblast activation. Eliminating senescent cells reduces fibrosis in multiple mouse models of age-related diseases. Early human trials with senolytics show promise for conditions with significant fibrotic components, such as idiopathic pulmonary fibrosis and diabetic kidney disease.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Fibrotic processes contribute significantly to age-related organ dysfunction. Research suggests that targeting glycation, TGF-β signaling, matrix remodeling enzymes, and senescent cells may help prevent or reverse pathological fibrosis.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 13,
      title: "F – Fibrosis & Structural Remodeling: Clinical Interventions",
      content: (
        <>
          <p>Evidence-based interventions to prevent excessive fibrosis and maintain tissue architecture include:</p>
          
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 my-6">
            <h3 className="text-xl font-medium text-indigo-900 mb-4">Evidence-Based Interventions for Fibrosis & Structural Remodeling</h3>
            
            <div className="space-y-5">
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-indigo-800 font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">AGE Reduction Strategies</h4>
                  <p className="text-gray-700 mb-2">Dietary AGE restriction (low-temperature, moisture-based cooking methods), avoiding heavily browned or charred foods can reduce AGE intake by 50-80%. Certain compounds like carnosine (1-2g daily), benfotiamine (300mg daily), and pyridoxamine (50-250mg daily) may inhibit AGE formation or increase their breakdown.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Provide specific culinary techniques to reduce dietary AGE intake. Consider carnosine supplementation, particularly for patients with diabetes or cardiovascular disease. Measure skin autofluorescence (if available) to assess AGE burden and monitor response to interventions.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-indigo-800 font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Collagen-Supporting Nutrients</h4>
                  <p className="text-gray-700 mb-2">Vitamin C (500-1000mg daily), copper (1-2mg daily), zinc (15-30mg daily), and specific amino acids (glycine, proline, lysine) support proper collagen synthesis and maturation. Collagen peptide supplements (10-15g daily) provide building blocks and may stimulate endogenous collagen production.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Assess micronutrient status, particularly vitamin C and minerals involved in collagen synthesis. Consider collagen peptides for patients with early signs of skin aging, joint deterioration, or impaired wound healing. Combine with vitamin C to enhance effectiveness.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-indigo-800 font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Anti-fibrotic Botanicals</h4>
                  <p className="text-gray-700 mb-2">Several botanicals show anti-fibrotic effects through various mechanisms. Curcumin (1-2g daily of bioavailable forms) inhibits TGF-β signaling. Milk thistle extract (silymarin, 420-800mg daily) reduces liver fibrosis in multiple studies. Ginseng (1-3g daily) shows anti-fibrotic effects in animal models and small human trials.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Consider these botanicals as adjuncts for patients with early fibrotic changes or risk factors for fibrotic diseases. Use bioavailable forms of curcumin (with piperine, phytosomal, or nanoparticle formulations) to achieve therapeutic levels. Monitor liver function when using high doses of botanical compounds.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-indigo-800 font-bold">4</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Mechanical Stimulation</h4>
                  <p className="text-gray-700 mb-2">Appropriate mechanical stimulation helps maintain healthy extracellular matrix. Resistance training (2-3 sessions/week) promotes collagen synthesis and muscle-tendon unit strength. Targeted soft tissue mobilization techniques help maintain fascial mobility and prevent adhesions.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Prescribe progressive resistance training programs appropriate for the patient's age and fitness level. Consider referring to physical therapists for specific soft tissue mobilization techniques for patients with restricted mobility or early fibrotic changes. Emphasize the importance of maintaining tissue elasticity through regular movement.</p>
                </div>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            Preventing excessive fibrosis requires a multi-faceted approach addressing glycation, collagen synthesis, inflammatory drivers, and mechanical factors. Many interventions have favorable risk-benefit profiles and can be incorporated into comprehensive longevity programs.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 14,
      title: "T – Timekeeping & Chronobiology: Overview",
      content: (
        <>
          <p>Biological rhythms govern virtually all aspects of physiology and behavior. The timekeeping domain encompasses the complex network of molecular clocks that synchronize cellular processes with environmental cycles, particularly the 24-hour light/dark cycle.</p>
          
          <div className="bg-gray-100 p-4 my-4 rounded-md">
            <h3 className="text-lg font-medium mb-3">Key Chronobiological Processes</h3>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong>Circadian rhythms:</strong> ~24-hour cycles in biological processes coordinated by the molecular clock</li>
              <li><strong>Clock genes:</strong> Core oscillatory machinery (CLOCK, BMAL1, PER, CRY) that drives circadian rhythms</li>
              <li><strong>Peripheral clocks:</strong> Tissue-specific circadian oscillators present in nearly all cells</li>
              <li><strong>Chronodisruption:</strong> Misalignment between internal clocks and environmental cycles</li>
              <li><strong>Circadian amplitude:</strong> The strength of circadian oscillations, which typically declines with age</li>
            </ul>
          </div>
          
          <p>With age, circadian systems tend to weaken and become less responsive to synchronizing cues, leading to fragmented activity patterns, disrupted sleep, and impaired temporal coordination of physiological processes.</p>
          
          <p>Chronodisruption is increasingly recognized as both a consequence of aging and a contributor to age-related diseases. Modern lifestyle factors like artificial light exposure, irregular eating patterns, and shift work can further compromise circadian function.</p>
          
          <KeyTakeaway>
            Robust circadian rhythms are essential for coordinating biochemical and physiological processes. Preserving and enhancing these biological timekeeping mechanisms may be critical for healthy aging.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 15,
      title: "T – Timekeeping & Chronobiology: Key Research Findings",
      content: (
        <>
          <p>Research has revealed the profound importance of circadian rhythms for health and longevity:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-blue-800 mb-2">Clock Genes and Longevity</h3>
              <p>Mouse models with mutations in core clock genes (CLOCK, BMAL1, PER) show accelerated aging phenotypes and shortened lifespans. Conversely, enhancing circadian function through lifestyle or pharmacological interventions extends healthspan in multiple model organisms. Human genetic studies link variants in clock genes to longevity, metabolic health, and disease risk. Centenarians often maintain robust circadian rhythms compared to age-matched controls.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-indigo-800 mb-2">Circadian Regulation of Metabolism</h3>
              <p>Up to 40% of the genome shows circadian oscillations in expression, including many metabolic genes. Time-restricted feeding (limiting food intake to specific hours) improves metabolic health in mice even without reducing calories. Human studies show that eating in alignment with circadian rhythms improves insulin sensitivity, lipid profiles, and weight management. Meal timing affects the thermic effect of food and substrate utilization.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-purple-800 mb-2">Chronodisruption and Disease Risk</h3>
              <p>Shift work and chronic circadian disruption are associated with increased risk of metabolic syndrome, cardiovascular disease, cancer, and neurodegenerative conditions. The World Health Organization has classified shift work as a probable carcinogen. Experimental studies show that chronic jet lag accelerates tumor growth in animal models. Even mild circadian disruption from social jet lag (weekend/weekday sleep schedule differences) is associated with metabolic dysfunction.</p>
            </div>
            <div className="bg-pink-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-pink-800 mb-2">Light Exposure and Circadian Health</h3>
              <p>Light is the primary synchronizer of the central circadian pacemaker. Morning bright light exposure improves sleep quality, mood, and cognitive performance. Evening blue light exposure from electronic devices suppresses melatonin production and delays circadian phase. Controlled studies show that blocking blue light in the evening improves sleep latency and quality. Maintaining regular light/dark cycles helps preserve circadian amplitude.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Circadian rhythm disruption represents an underappreciated driver of aging and age-related disease. Research suggests that maintaining robust circadian rhythms through lifestyle interventions may promote longevity and resilience.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 16,
      title: "T – Timekeeping & Chronobiology: Clinical Interventions",
      content: (
        <>
          <p>Evidence-based interventions to optimize circadian function include:</p>
          
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 my-6">
            <h3 className="text-xl font-medium text-blue-900 mb-4">Evidence-Based Interventions for Circadian Health</h3>
            
            <div className="space-y-5">
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Light Hygiene</h4>
                  <p className="text-gray-700 mb-2">Morning bright light exposure (30-60 minutes, ideally outdoors or with 10,000 lux light therapy) strengthens circadian rhythms. Evening blue light avoidance (amber glasses, screen filters, or device settings) 2-3 hours before bedtime preserves melatonin production. Maintaining consistent light/dark patterns helps entrain the circadian system.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Recommend morning outdoor walks without sunglasses. Prescribe light therapy for patients with seasonal affective disorder or circadian rhythm disorders. Suggest blue-blocking glasses for evening use, especially for patients with sleep onset difficulties. Emphasize maintaining a dark bedroom environment.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Time-Restricted Eating</h4>
                  <p className="text-gray-700 mb-2">Limiting food intake to an 8-10 hour window, ideally aligned with daylight hours, improves metabolic health in multiple studies. The specific timing appears important – early time-restricted eating (finishing dinner by early evening) shows particular metabolic benefits compared to late-day eating patterns.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Start patients with a 12-hour eating window and gradually narrow to 8-10 hours as tolerated. Recommend earlier meal timing when possible (e.g., 8am-6pm rather than 12pm-10pm). Consider individual chronotype when making recommendations. Track metabolic parameters (insulin, lipids, blood pressure) to assess response.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Circadian-Supportive Supplements</h4>
                  <p className="text-gray-700 mb-2">Melatonin (0.3-3mg) taken 2-3 hours before desired sleep onset can help entrain circadian rhythms, particularly in older adults with diminished endogenous production. REV-ERB agonists like synthetic ligands or natural compounds (e.g., GSK4112 or derivatives) show promise in animal models but remain investigational in humans.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Consider low-dose melatonin (start at 0.3mg) for older patients with sleep onset difficulties, particularly those with evidence of circadian dysregulation. Emphasize proper timing rather than higher doses. Measure DLMO (dim light melatonin onset) when available to determine optimal administration time.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">4</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Temperature Rhythms</h4>
                  <p className="text-gray-700 mb-2">Core body temperature follows a circadian pattern that helps regulate sleep-wake cycles. Evening temperature reduction (by 1-2°C) through environmental cooling or warm baths (which paradoxically lowers core temperature) can enhance sleep quality and circadian entrainment.</p>
                  <p className="text-sm text-gray-600"><strong>Implementation:</strong> Recommend bedroom temperatures of 65-68°F (18-20°C) for optimal sleep. Suggest warm baths or showers 1-2 hours before bedtime to facilitate the natural evening drop in core temperature. Consider temperature-regulating sleep products for patients with temperature dysregulation or night sweats.</p>
                </div>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            Optimizing circadian rhythms offers a powerful approach to improving sleep, metabolism, and overall health. Simple behavioral interventions like light management and meal timing can have profound effects on circadian function and may contribute to healthy aging.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 17,
      title: "Integrating the SHIFT Framework: Clinical Applications",
      content: (
        <>
          <p>The SHIFT framework provides a comprehensive approach to addressing the cellular basis of aging and age-related diseases. In clinical practice, these domains frequently interact and overlap, requiring an integrated approach:</p>
          
          <div className="bg-gray-100 p-4 my-4 rounded-md">
            <h3 className="text-lg font-medium mb-3">Clinical Approach to the SHIFT Framework</h3>
            <ol className="list-decimal list-inside space-y-2 ml-2">
              <li><strong>Assessment:</strong> Evaluate each domain through targeted history, examination, and biomarkers</li>
              <li><strong>Prioritization:</strong> Identify the most dysregulated domains based on patient-specific findings</li>
              <li><strong>Intervention sequencing:</strong> Address fundamental domains first (often Sensing and Timekeeping)</li>
              <li><strong>Monitoring:</strong> Track domain-specific biomarkers and clinical parameters to assess response</li>
              <li><strong>Adjustment:</strong> Refine interventions based on response patterns and emergent priorities</li>
            </ol>
          </div>
          
          <p>Many interventions affect multiple SHIFT domains simultaneously. For example, time-restricted eating impacts both Sensing (through metabolic fuel-gauging pathways) and Timekeeping (through circadian entrainment of peripheral clocks). Understanding these interactions helps optimize intervention selection and sequencing.</p>
          
          <p>The SHIFT framework also helps explain why some interventions may be particularly synergistic. For instance, combining senolytic therapies (targeting the Inflammation domain) with anti-fibrotic approaches (targeting the Fibrosis domain) may be more effective for addressing age-related tissue dysfunction than either approach alone.</p>
          
          <KeyTakeaway>
            The SHIFT framework provides a practical scaffold for organizing clinical assessment and intervention in longevity medicine. By systematically addressing each domain while recognizing their interactions, clinicians can develop comprehensive, personalized approaches to promoting healthy aging.
          </KeyTakeaway>
        </>
      )
    }
  ];

  return (
    <MentorshipAccessGate>
      <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 flex-grow max-w-5xl">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            className="flex items-center text-[#3498DB]"
            onClick={() => navigate("/mentorship")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Mentorship
          </Button>
          
          <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
            Lecture Material
          </div>
        </div>
        
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">A Cellular Framework For Longevity: The SHIFT Approach</h1>
          <p className="text-lg text-gray-600">Understanding the five fundamental domains that govern cellular health and aging</p>
        </div>
        
        <div>
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Course Overview</h2>
          <p className="text-gray-700 mb-4">
            This lecture introduces the SHIFT framework for understanding and addressing the cellular basis of aging and longevity. By exploring the five key domains that govern cellular health – Sensing, Homeostatic defense, Inflammation, Fibrosis, and Timekeeping – clinicians can develop more effective strategies for promoting healthy aging and preventing age-related diseases.
          </p>
          
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
            <h3 className="text-md font-medium text-blue-800 mb-2 flex items-center">
              <Info className="h-5 w-5 mr-2" />
              Learning Objectives
            </h3>
            <ul className="list-disc list-inside text-blue-700 space-y-1">
              <li>Understand the five domains of the SHIFT framework and their role in cellular aging</li>
              <li>Explore key research findings supporting each domain's contribution to longevity</li>
              <li>Learn evidence-based clinical interventions that target specific SHIFT domains</li>
              <li>Develop an integrated approach to assessing and addressing cellular health in clinical practice</li>
            </ul>
          </div>
          
          <div className="my-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Lecture Slides</h2>
            <p className="text-gray-600 mb-4">
              The slide deck below contains the complete lecture content. Navigate through the slides to explore each aspect of the SHIFT framework in detail.
            </p>
            
            <Slideshow slides={lectureSlides} />
          </div>
        </div>
      </div>
      
      <Footer />
      </div>
    </MentorshipAccessGate>
  );
}

export default CellularFrameworkLongevity;